import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useSettings } from "@/hooks/use-settings";
import { useAuth } from "@/hooks/useAuth";
import { useCartStore } from "@/stores/cart-store";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { useFilterStore } from "@/stores/filter-store";
import { GradeBadge } from "./grade-badge";
import { formatMoney, parsePrice } from "@shared/currency";
import {
  getDisplayBrand,
  getCatalogContributingBrands,
  matchesBrandFilter,
  isFactoryRefurbished,
  FACTORY_REFURBISHED_LABEL,
  FACTORY_REFURBISHED_GRADING_ANCHOR,
  PALLET_GRADE_LABEL,
  getCatalogGradeLabels,
} from "@shared/palletDisplay";
import { ProductCard } from "./product-card";
import { SubcategoryFilterBlock } from "./catalog-sidebar/subcategory-filter-block";
import { ActiveFilterChips, type ActiveChip } from "./catalog-sidebar/active-filter-chips";
import { Search, Grid3x3, List, MapPin, ChevronLeft, ChevronRight, ChevronDown, SlidersHorizontal, X, Mail, Loader2, Sparkles, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Link } from "wouter";
import { PalletMiniHero } from "./pallet-composition-card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { trackSelectItem } from "@/lib/analytics";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { getCategoryIcon } from "@/lib/categoryIcons";
import { useProductTypeImages } from "@/lib/productTypeImages";
import { resolveCategoryAttributeSchema, formatAttributeChip } from "@shared/categoryAttributes";

const VALID_ITEMS_PER_PAGE = [25, 50, 100];

// Safe JSON parser for attributes - returns empty object on error
function safeParseAttributes(attributes: any): Record<string, any> {
  if (!attributes) return {};
  if (typeof attributes === 'object') return attributes;
  try {
    return JSON.parse(attributes);
  } catch (e) {
    console.warn('Failed to parse product attributes:', e);
    return {};
  }
}

// Map productType to category slug for icon lookup
const productTypeToCategorySlug: Record<string, string> = {
  'treadmill': 'treadmills',
  'elliptical': 'ellipticals',
  'spin-bike': 'spin-bikes',
  'recumbent-bike': 'recumbent-bikes',
  'upright-bike': 'upright-bikes',
  'max-trainer': 'max-trainers',
  'home-gym': 'home-gyms',
  'weight-bench': 'weight-benches',
  'rower': 'rowing-machines',
  'kettlebell': 'kettlebells',
  'dumbbell': 'strength-equipment',
  'barbell': 'strength-equipment',
  'weight-stand': 'strength-equipment',
  'fitness-accessory': 'fitness-equipment',
};

// Hero Slide type from API
interface HeroSlideData {
  id: string;
  name: string;
  status: 'active' | 'coming_soon';
  mode: 'fixed' | 'random';
  headline: string | null;
  subheadline: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  phrasePool: string[] | null;
  subheadlinePool: string[] | null;
  enabled: boolean;
  categoryHeroImage?: { imageUrl: string | null } | null;
  productTypeImage?: { imageUrl: string | null } | null;
}

// Unified Hero Carousel - uses admin-configured hero slides
// Note: slides are passed in to avoid duplicate fetching when used with wrapper
function HeroCarousel({ slides }: { slides: HeroSlideData[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [randomContent, setRandomContent] = useState<{ headline: string; subheadline: string } | null>(null);
  
  // Filter to only slides with valid images
  const validSlides = slides.filter(s => {
    const imageUrl = s.categoryHeroImage?.imageUrl || s.productTypeImage?.imageUrl;
    return s.enabled && imageUrl;
  });
  
  // Handle random content selection on slide change
  useEffect(() => {
    if (validSlides.length === 0) return;
    const slide = validSlides[currentIndex];
    if (!slide) return;
    
    if (slide.mode === 'random') {
      const phrases = slide.phrasePool || [];
      const subheadlines = slide.subheadlinePool || [];
      setRandomContent({
        headline: phrases.length > 0 ? phrases[Math.floor(Math.random() * phrases.length)] : '',
        subheadline: subheadlines.length > 0 ? subheadlines[Math.floor(Math.random() * subheadlines.length)] : '',
      });
    } else {
      setRandomContent(null);
    }
  }, [currentIndex, validSlides]);
  
  // Auto-advance carousel
  useEffect(() => {
    if (validSlides.length <= 1) return;
    
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % validSlides.length);
        setIsTransitioning(false);
      }, 300);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [validSlides.length]);
  
  if (validSlides.length === 0) return null;
  
  const currentSlide = validSlides[currentIndex];
  const imageUrl = currentSlide.categoryHeroImage?.imageUrl || currentSlide.productTypeImage?.imageUrl;
  
  // Determine headline/subheadline based on mode
  const headline = currentSlide.mode === 'random' ? randomContent?.headline : currentSlide.headline;
  const subheadline = currentSlide.mode === 'random' ? randomContent?.subheadline : currentSlide.subheadline;
  
  return (
    <div className="mb-6 rounded-xl overflow-hidden relative" data-testid="hero-carousel">
      <div className="relative w-full h-64 md:h-80">
        {/* Background Image */}
        <img 
          src={imageUrl!}
          alt={currentSlide.name}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
        />
        
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        
        {/* Text Content */}
        <div className={`absolute inset-0 flex flex-col justify-center px-8 md:px-12 text-white transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          {/* Coming Soon Badge */}
          {currentSlide.status === 'coming_soon' && (
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500 text-black text-xs font-bold uppercase tracking-wide rounded-full shadow-lg">
                <Sparkles className="h-3.5 w-3.5" />
                Coming Soon
              </span>
            </div>
          )}
          {headline && (
            <h2 className="text-2xl md:text-4xl font-bold mb-2 max-w-lg drop-shadow-lg">
              {headline}
            </h2>
          )}
          {subheadline && (
            <p className="text-sm md:text-lg text-slate-200 max-w-md mb-4 drop-shadow">
              {subheadline}
            </p>
          )}
          {currentSlide.ctaLabel && currentSlide.ctaUrl && (
            <Link href={currentSlide.ctaUrl}>
              <Button 
                className={currentSlide.status === 'coming_soon' 
                  ? "bg-amber-500 hover:bg-amber-400 text-black w-fit font-medium"
                  : "bg-primary hover:bg-primary/90 text-white w-fit"
                }
                data-testid="button-hero-cta"
              >
                {currentSlide.ctaLabel}
              </Button>
            </Link>
          )}
        </div>
        
        {/* Dots indicator */}
        {validSlides.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {validSlides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentIndex(idx);
                    setIsTransitioning(false);
                  }, 150);
                }}
                className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-5' : 'bg-white/50 hover:bg-white/70'}`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
        
        {/* Navigation arrows */}
        {validSlides.length > 1 && (
          <>
            <button
              onClick={() => {
                setIsTransitioning(true);
                setTimeout(() => {
                  setCurrentIndex((prev) => (prev - 1 + validSlides.length) % validSlides.length);
                  setIsTransitioning(false);
                }, 150);
              }}
              className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => {
                setIsTransitioning(true);
                setTimeout(() => {
                  setCurrentIndex((prev) => (prev + 1) % validSlides.length);
                  setIsTransitioning(false);
                }, 150);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Wrapper component that shows HeroCarousel if slides exist, otherwise falls back to JohnsonHeroBanner
function HeroCarouselWithFallback({ 
  visibleCategoryIds, 
  fallbackImages,
  categoryIconUrls,
  categories
}: { 
  visibleCategoryIds?: string[];
  fallbackImages: { id: string; productType: string; displayName: string; imageUrl: string | null }[];
  categoryIconUrls?: Record<string, string>;
  categories?: { id: string; slug: string; name: string; visible: boolean }[];
}) {
  // Fetch hero slides filtered by visible categories
  const queryParams = visibleCategoryIds?.length 
    ? `?visibleCategoryIds=${visibleCategoryIds.join(',')}`
    : '';
  
  const { data: slides = [], isLoading } = useQuery<HeroSlideData[]>({
    queryKey: ['/api/hero-slides', visibleCategoryIds],
    queryFn: async () => {
      const res = await fetch(`/api/hero-slides${queryParams}`);
      if (!res.ok) throw new Error('Failed to fetch hero slides');
      return res.json();
    },
  });
  
  // Filter to only slides with valid images
  const validSlides = slides.filter(s => {
    const imageUrl = s.categoryHeroImage?.imageUrl || s.productTypeImage?.imageUrl;
    return s.enabled && imageUrl;
  });
  
  // While loading, show nothing to avoid flicker
  if (isLoading) return null;
  
  // If we have admin-configured slides, use the new carousel
  if (validSlides.length > 0) {
    return <HeroCarousel slides={slides} />;
  }
  
  // Fallback to legacy Johnson banner if no slides configured
  if (fallbackImages.length > 0) {
    return (
      <JohnsonHeroBanner 
        images={fallbackImages}
        categoryIconUrls={categoryIconUrls}
        categories={categories}
      />
    );
  }
  
  return null;
}

// Johnson Fitness Hero Banner - cycles through equipment type images (legacy fallback)
function JohnsonHeroBanner({ images, categoryIconUrls, categories }: { 
  images: { id: string; productType: string; displayName: string; imageUrl: string | null }[];
  categoryIconUrls?: Record<string, string>;
  categories?: { id: string; slug: string; name: string }[];
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const validImages = images.filter(img => img.imageUrl);
  
  useEffect(() => {
    if (validImages.length <= 1) return;
    
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % validImages.length);
        setIsTransitioning(false);
      }, 300);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [validImages.length]);
  
  if (validImages.length === 0) return null;
  
  const currentImage = validImages[currentIndex];
  
  return (
    <div className="mb-6 rounded-xl overflow-hidden relative bg-gradient-to-r from-slate-900 to-slate-800" data-testid="hero-banner-johnson">
      <div className="flex flex-col md:flex-row items-center">
        {/* Image Section */}
        <div className="relative w-full md:w-1/2 h-48 md:h-64 overflow-hidden">
          <img 
            src={currentImage.imageUrl!}
            alt={currentImage.displayName}
            className={`w-full h-full object-contain transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
          />
          {/* Dots indicator */}
          {validImages.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {validImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-4' : 'bg-white/50'}`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Text Section */}
        <div className="w-full md:w-1/2 p-6 text-white">
          <p className="text-xs uppercase tracking-wider text-green-400 mb-2">New Arrivals</p>
          <h3 className="text-2xl md:text-3xl font-bold mb-2">Exercise Equipment</h3>
          <p className="text-slate-300 text-sm md:text-base mb-4">
            Premium fitness equipment from Bowflex, Schwinn, Horizon & more. All units fully functional with manufacturer warranty.
          </p>
          <div className={`flex items-center gap-4 transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            {(() => {
              // Look up the AI-generated category icon
              const categorySlug = productTypeToCategorySlug[currentImage.productType];
              const category = categories?.find(c => c.slug === categorySlug);
              const iconUrl = category ? categoryIconUrls?.[category.id] : null;
              
              if (iconUrl) {
                return (
                  <img 
                    src={iconUrl} 
                    alt={currentImage.displayName}
                    className="h-12 w-12 md:h-16 md:w-16 object-contain rounded-lg bg-white/10 p-1"
                  />
                );
              }
              // Fallback to Lucide icon if no AI icon available
              const CategoryIcon = getCategoryIcon(currentImage.productType);
              return <CategoryIcon className="h-10 w-10 md:h-12 md:w-12 text-green-400" />;
            })()}
            <span className="text-2xl md:text-3xl font-bold text-green-400">
              {currentImage.displayName}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Isolated newsletter component - prevents parent re-renders when typing
function NewsletterSignup() {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || loading) return;
    
    setLoading(true);
    try {
      const data = await apiRequest('POST', '/api/newsletter/subscribe', { 
        email, 
        source: 'showroom' 
      });
      toast({
        title: "Success",
        description: data.message || "Thanks for subscribing!",
      });
      setSubscribed(true);
      setEmail('');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-muted/50 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-foreground text-sm">Stay Updated</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Get notified about new inventory and exclusive deals.
      </p>
      {subscribed ? (
        <div className="text-sm text-green-600 font-medium" data-testid="text-newsletter-success">
          Thanks for subscribing!
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-2">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="text-sm"
            data-testid="input-newsletter-email"
          />
          <Button 
            type="submit" 
            className="w-full text-sm"
            disabled={loading || !email}
            data-testid="button-newsletter-subscribe"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Subscribing...
              </>
            ) : (
              'Subscribe'
            )}
          </Button>
        </form>
      )}
    </div>
  );
}

export function ProductGallery() {
  const { visibleCategories } = useSettings();
  const { user } = useAuth();
  const isAdmin = user?.roles?.some((r: string) => ['SUPER_ADMIN', 'ADMIN', 'OPERATIONS'].includes(r)) ?? false;
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const { getWarehouseCodes } = useCartStore();
  const warehouseCodesInCart = getWarehouseCodes();
  const { setLastFilterParams } = useFilterStore();
  
  // Fetch product type images once at gallery level for efficient fallback
  const { data: productTypeImages } = useProductTypeImages();
  
  // URL is the single source of truth for filters and pagination
  const { filters, pagination, updateUrl } = useUrlFilters();
  const { currentPage, itemsPerPage } = pagination;

  // Local state for price slider to prevent excessive history updates
  const [localPriceRange, setLocalPriceRange] = useState<number[]>(filters.priceRange);
  const priceUpdateTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Local state for search input to prevent scroll-to-top on every keystroke
  const [localSearchTerm, setLocalSearchTerm] = useState(filters.searchTerm);
  const searchUpdateTimerRef = useRef<NodeJS.Timeout | null>(null);
  

  // Save filter params to store whenever they change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (filters.warehouseLocations.length > 0) {
      params.set('warehouses', filters.warehouseLocations.join(','));
    }
    if (filters.grades.length > 0) {
      params.set('grades', filters.grades.join(','));
    }
    if (filters.brands.length > 0) {
      params.set('brands', filters.brands.join(','));
    }
    if (filters.categories.length > 0) {
      params.set('categories', filters.categories.join(','));
    }
    if (filters.priceRange[0] !== 0 || filters.priceRange[1] !== 5000) {
      params.set('priceMin', String(filters.priceRange[0]));
      params.set('priceMax', String(filters.priceRange[1]));
    }
    if (filters.inStockOnly) {
      params.set('inStock', 'true');
    }
    if (filters.searchTerm) {
      params.set('search', filters.searchTerm);
    }
    if (pagination.currentPage > 1) {
      params.set('page', String(pagination.currentPage));
    }
    if (pagination.itemsPerPage !== 25) {
      params.set('per_page', String(pagination.itemsPerPage));
    }

    setLastFilterParams(params.toString());
  }, [filters, pagination, setLastFilterParams]);

  // Sync local price range with URL when URL changes externally
  useEffect(() => {
    setLocalPriceRange(filters.priceRange);
  }, [filters.priceRange]);
  
  // Sync local search term with URL when URL changes externally
  useEffect(() => {
    setLocalSearchTerm(filters.searchTerm);
  }, [filters.searchTerm]);

  const { data: inventory, isLoading, error } = useQuery<any[]>({
    queryKey: ['/api/inventory'],
    select: (data: any[]) => {
      // Group inventory by model and calculate starting price and available grades
      const modelMap = new Map();
      
      data.forEach((item: any) => {
        const key = item.model.id;
        if (!modelMap.has(key)) {
          modelMap.set(key, {
            id: key,
            brand: item.model.brand,
            modelName: item.model.mfgSku,
            description: item.model.description,
            imageUrl: item.model.imageUrl,
            category: item.model.category,
            color: item.model.color,
            dimensionsWidth: item.model.dimensionsWidth,
            dimensionsDepth: item.model.dimensionsDepth,
            dimensionsHeight: item.model.dimensionsHeight,
            weightLbs: item.model.weightLbs,
            msrp: item.model.msrp,
            attributes: item.model.attributes,
            minOrderQty: item.model.minOrderQty,
            orderMultiple: item.model.orderMultiple,
            isPallet: item.model.isPallet,
            isPalletOnly: item.model.isPalletOnly,
            boxType: item.model.boxType,
            palletConstituentBrands: item.model.palletConstituentBrands,
            palletConstituentBrandQuantities: item.model.palletConstituentBrandQuantities,
            palletConstituentImages: item.model.palletConstituentImages,
            palletConstituentModelCount: item.model.palletConstituentModelCount,
            palletPerItemStartingPriceCents: item.model.palletPerItemStartingPriceCents,
            palletTotalUnits: item.model.palletTotalUnits,
            prices: [],
            grades: [],
            gradePrices: {},
            inStockGradePrices: {},
            inventory: [],
            warehouseCodes: [],
            remoteWarehouseCodes: [],
            isSoldOut: true,
            // Sum of in-stock units across this model's inventory rows.
            // Drives the "N pallets available" / "N units available" line
            // under the wholesale price on the catalog card.
            availableQuantity: 0,
          });
        }
        
        const model = modelMap.get(key);
        
        // Always collect price data for display, even for out-of-stock items
        const price = parseFloat(item.sellingPrice);
        if (price > 0) {
          model.prices.push(price);
          if (!model.gradePrices[item.grade] || price < model.gradePrices[item.grade]) {
            model.gradePrices[item.grade] = price;
          }
        }

        // Determine availability separately from price display:
        // - For A-grade and B-grade (bulk): Check quantity > 0
        // - For B+/B- grades: Check unsoldSerializedUnitCount > 0 (tracked individually in serialized_units)
        // - presaleUnitCount > 0 means units exist but aren't in a warehouse location yet (any grade)
        const isBulkGrade = item.grade === 'A' || item.grade === 'A-' || item.grade === 'B';
        const hasInventory = isBulkGrade
          ? (item.isAvailable && item.quantity > 0)
          : (item.unsoldSerializedUnitCount && item.unsoldSerializedUnitCount > 0);
        // Presale: physically un-received units that are orderable (any grade, including A)
        const hasPresaleInventory = (item.presaleUnitCount && item.presaleUnitCount > 0);

        if (hasInventory || hasPresaleInventory) {
          model.isSoldOut = false;
          model.grades.push(item.grade);
          model.inventory.push(item);
          // Track availability buckets at the model level so the gallery can
          // show "In Stock Now" vs "Arriving Soon" filters.
          if (hasInventory) {
            model.hasInStockUnits = true;
            const units = isBulkGrade
              ? Number(item.quantity) || 0
              : Number(item.unsoldSerializedUnitCount) || 0;
            if (units > 0) model.availableQuantity += units;
          }
          if (hasPresaleInventory) {
            model.hasPresaleUnits = true;
          }
          // Track prices (both in-stock and presale contribute to startingPrice)
          if (price > 0) {
            if (!model.inStockGradePrices[item.grade] || price < model.inStockGradePrices[item.grade]) {
              model.inStockGradePrices[item.grade] = price;
            }
          }

          // Track warehouse codes
          if (item.address?.warehouseCode && !model.warehouseCodes.includes(item.address.warehouseCode)) {
            model.warehouseCodes.push(item.address.warehouseCode);
          }

          // Track remote-warehouse codes specifically (drives the "Ships from {code}"
          // label on the customer product card). Any inventory row classified as
          // serialized_remote OR bulk_quantity_remote contributes its code here.
          if (
            (item.inventoryType === 'serialized_remote' || item.inventoryType === 'bulk_quantity_remote') &&
            item.address?.warehouseCode &&
            !model.remoteWarehouseCodes.includes(item.address.warehouseCode)
          ) {
            model.remoteWarehouseCodes.push(item.address.warehouseCode);
          }
        }
      });

      return Array.from(modelMap.values())
        .map(model => {
          // Calculate starting price from in-stock grades only (B- -> B+ -> B -> A)
          const isp = model.inStockGradePrices;
          let startingPrice = 0;
          if (isp['B-']) {
            startingPrice = isp['B-'];
          } else if (isp['B+']) {
            startingPrice = isp['B+'];
          } else if (isp['B']) {
            startingPrice = isp['B'];
          } else if (isp['A']) {
            startingPrice = isp['A'];
          } else if (model.prices.length > 0) {
            startingPrice = Math.min(...model.prices);
          }
          
          return {
            ...model,
            startingPrice,
            hasInStockUnits: !!model.hasInStockUnits,
            hasPresaleUnits: !!model.hasPresaleUnits,
            availableGrades: Array.from(new Set(model.grades)) as Array<"A" | "B+" | "B-">
          };
        });
    }
  });

  // Fetch distribution centers
  const { data: distributionCenters, isLoading: distributionCentersLoading } = useQuery<any[]>({
    queryKey: ['/api/distribution-centers'],
  });

  // Fetch AI-generated category icons
  const { data: categoryIconUrls } = useQuery<Record<string, string>>({
    queryKey: ['/api/category-icons'],
  });

  // Fetch categories for icon lookup in hero banner
  const { data: allCategories } = useQuery<{ id: string; slug: string; name: string; visible: boolean; attributesSchema?: any }[]>({
    queryKey: ['/api/categories'],
  });

  // Fetch models that have coming-soon (expected) inventory but no current in-stock units
  const { data: comingSoonModels } = useQuery<any[]>({
    queryKey: ['/api/inventory/coming-soon-models'],
  });

  // Merge in-stock inventory with coming-soon-only models (those not already in inventory)
  const allProducts = useMemo(() => {
    if (!inventory) return undefined;
    if (!comingSoonModels || comingSoonModels.length === 0) return inventory;

    const existingModelIds = new Set(inventory.map((p: any) => p.id));
    const comingSoonOnly = comingSoonModels
      .filter((m: any) => !existingModelIds.has(m.id))
      .map((m: any) => ({
        id: m.id,
        brand: m.brand,
        modelName: m.modelName,
        description: m.description,
        imageUrl: m.imageUrl,
        category: m.category,
        color: m.color,
        dimensionsWidth: m.dimensionsWidth,
        dimensionsDepth: m.dimensionsDepth,
        dimensionsHeight: m.dimensionsHeight,
        weightLbs: m.weightLbs,
        msrp: m.msrp,
        attributes: m.attributes,
        minOrderQty: m.minOrderQty,
        orderMultiple: m.orderMultiple,
        isPallet: m.isPallet,
        isPalletOnly: m.isPalletOnly,
        boxType: m.boxType,
        palletConstituentBrands: m.palletConstituentBrands,
        palletConstituentBrandQuantities: m.palletConstituentBrandQuantities,
        palletConstituentImages: m.palletConstituentImages,
        palletConstituentModelCount: m.palletConstituentModelCount,
        palletPerItemStartingPriceCents: m.palletPerItemStartingPriceCents,
        palletTotalUnits: m.palletTotalUnits,
        availableGrades: (m.grades || []) as Array<"A" | "B+" | "B-">,
        grades: m.grades || [],
        gradePrices: {} as Record<string, number>,
        inStockGradePrices: {} as Record<string, number>,
        prices: [] as number[],
        warehouseCodes: m.warehouseCodes || [],
        startingPrice: 0,
        isSoldOut: false,
        isComingSoonOnly: true,
        hasInStockUnits: false,
        hasPresaleUnits: false,
        earliestShipDate: m.earliestShipDate || null,
      }));

    return [...inventory, ...comingSoonOnly];
  }, [inventory, comingSoonModels]);

  // Filter distribution centers to only show warehouses that have products in the current inventory
  // This ensures warehouses for hidden categories (like refrigerators) don't show when only exercise equipment is visible
  const activeWarehouses = useMemo(() => {
    if (!distributionCenters || !inventory) return [];
    
    // Collect all warehouse codes that have products in the inventory
    const warehouseCodesWithProducts = new Set<string>();
    inventory.forEach((product: any) => {
      if (product.warehouseCodes) {
        product.warehouseCodes.forEach((code: string) => {
          warehouseCodesWithProducts.add(code);
        });
      }
    });
    
    // Filter distribution centers to only those with products
    return distributionCenters.filter((dc: any) => 
      warehouseCodesWithProducts.has(dc.warehouseCode)
    );
  }, [distributionCenters, inventory]);

  // Calculate dynamic price range from inventory (round down min, round up max)
  const catalogPriceBounds = useMemo(() => {
    if (!inventory || inventory.length === 0) {
      return { min: 0, max: 5000 };
    }
    
    const allPrices = inventory.flatMap((product: any) => product.prices || []);
    if (allPrices.length === 0) {
      return { min: 0, max: 5000 };
    }

    const minPrice = Math.min(...allPrices);
    const maxPrice = Math.max(...allPrices);
    
    // Round down to nearest 50 for min, round up to nearest 50 for max
    let roundedMin = Math.floor(minPrice / 50) * 50;
    let roundedMax = Math.ceil(maxPrice / 50) * 50;
    
    // Handle edge case where min === max (ensure valid slider range)
    if (roundedMin === roundedMax) {
      roundedMax = roundedMin + 50;
    }
    
    return { min: roundedMin, max: roundedMax };
  }, [inventory]);

  // Sync localPriceRange with catalogPriceBounds when bounds change
  useEffect(() => {
    if (catalogPriceBounds) {
      const clampedMin = Math.max(catalogPriceBounds.min, Math.min(filters.priceRange[0], catalogPriceBounds.max));
      const clampedMax = Math.min(catalogPriceBounds.max, Math.max(filters.priceRange[1], catalogPriceBounds.min));
      
      // Only update if values changed
      if (clampedMin !== filters.priceRange[0] || clampedMax !== filters.priceRange[1]) {
        const newRange = [clampedMin, clampedMax];
        setLocalPriceRange(newRange);
        updateUrl({ filters: { priceRange: newRange }, resetPage: false });
      }
    }
  }, [catalogPriceBounds]);

  // Debounced price range update handler
  const handlePriceRangeChange = (value: number[]) => {
    setLocalPriceRange(value);
    
    // Clear existing timer
    if (priceUpdateTimerRef.current) {
      clearTimeout(priceUpdateTimerRef.current);
    }
    
    // Set new timer to update URL after 300ms of no slider movement
    priceUpdateTimerRef.current = setTimeout(() => {
      updateUrl({ filters: { priceRange: value }, resetPage: true });
    }, 300);
  };

  // Debounced search term update handler
  const handleSearchChange = (value: string) => {
    setLocalSearchTerm(value);
    
    // Clear existing timer
    if (searchUpdateTimerRef.current) {
      clearTimeout(searchUpdateTimerRef.current);
    }
    
    // Set new timer to update URL after 300ms of no typing
    searchUpdateTimerRef.current = setTimeout(() => {
      updateUrl({ filters: { searchTerm: value }, resetPage: true });
    }, 300);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (priceUpdateTimerRef.current) {
        clearTimeout(priceUpdateTimerRef.current);
      }
      if (searchUpdateTimerRef.current) {
        clearTimeout(searchUpdateTimerRef.current);
      }
    };
  }, []);

  const filteredProducts = allProducts?.filter((product: any) => {
    // Category visibility filter (hide products from categories not in visible list)
    if (visibleCategories.length > 0 && product.category?.id && !visibleCategories.includes(product.category.id)) {
      return false;
    }
    
    // Search filter (use URL-derived value to prevent scroll reset during typing)
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch = 
        product.brand.toLowerCase().includes(searchLower) ||
        product.modelName.toLowerCase().includes(searchLower) ||
        product.description.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    // Brand filter (multi-select) - check both global brands and category-specific brands.
    // Pallet products use their constituent brands instead of the seller brand "OpenBox"
    // so picking e.g. "LG" surfaces every pallet that contains LG units.
    const categoryBrands = filters.categoryBrands || {};
    const productCatId = product.category?.id;

    // Global brand filter - applies to all products (case-insensitive)
    if (filters.brands.length > 0) {
      if (!matchesBrandFilter(product, filters.brands)) {
        return false;
      }
    }

    // Category-specific brand filter - when ANY category has selections, ONLY show matching products
    const hasAnyCategoryBrandFilters = Object.keys(categoryBrands).length > 0;
    if (hasAnyCategoryBrandFilters) {
      const categoryIsInFilter = productCatId ? (productCatId in categoryBrands) : false;
      const catBrandsForProduct = productCatId ? (categoryBrands[productCatId] || []) : [];

      if (!categoryIsInFilter) {
        // Category has never been explicitly added to the filter (e.g. a new category
        // added to visible_categories after the user's filter was saved).
        // Allow it through so new categories show automatically.
      } else if (catBrandsForProduct.length === 0) {
        // Category IS in the filter but has been explicitly emptied - hide all its products.
        // (parseRecordList preserves empty arrays so this distinction survives URL roundtrips.)
        return false;
      } else {
        if (!matchesBrandFilter(product, catBrandsForProduct)) {
          return false;
        }
      }
    }

    // Category filter (multi-select)
    if (filters.categories.length > 0 && !filters.categories.includes(product.category?.id)) {
      return false;
    }

    // Grade filter — semantics: per-subcategory `categoryGrades[catId]` is the
    // INCLUDED list of grade labels for that subcategory. If the entry is
    // missing, the subcategory defaults to "all included" (legacy default).
    // Use the same catalog grade labels we aggregate into the sidebar so
    // factory-refurbished pallets match against PALLET_GRADE_LABEL rather
    // than their raw cosmetic "B".
    const categoryGrades = filters.categoryGrades || {};
    const productCatalogGrades = getCatalogGradeLabels(product, product.availableGrades);
    if (
      productCatId &&
      productCatId in categoryGrades &&
      productCatalogGrades.length > 0
    ) {
      const includedGrades = categoryGrades[productCatId];
      const hasIncludedGrade = productCatalogGrades.some((g: string) =>
        includedGrades.includes(g),
      );
      if (!hasIncludedGrade) return false;
    }
    // Legacy global ?grades= retained as EXCLUDED list so old shared bookmarks
    // continue to work until the migration effect rewrites them into per-cat.
    if (filters.grades.length > 0 && productCatalogGrades.length > 0) {
      const hasNonExcludedGrade = productCatalogGrades.some(
        (grade: string) => !filters.grades.includes(grade),
      );
      if (!hasNonExcludedGrade) return false;
    }

    // Warehouse location filter (multi-select)
    if (filters.warehouseLocations.length > 0) {
      const hasMatchingWarehouse = filters.warehouseLocations.some(
        warehouseCode => product.warehouseCodes.includes(warehouseCode)
      );
      if (!hasMatchingWarehouse) return false;
    }
    
    // Coming-soon-only models bypass the price filter (they have no price yet)
    // but do respect the "In Stock Only" toggle
    if (product.isComingSoonOnly) {
      if (filters.inStockOnly) return false;
    } else {
      // Price filter — hide $0 (unpriced) items and items outside selected range
      if (product.startingPrice <= 0 || product.startingPrice < filters.priceRange[0] || product.startingPrice > filters.priceRange[1]) {
        return false;
      }

      // Hide sold-out items for everyone (admins see the same view as customers).
      if (product.isSoldOut) {
        return false;
      }
    }

    // Availability filter — distinguishes products with units in the warehouse
    // ("In Stock Now") from those that only have presale / arriving / coming-soon
    // units ("Arriving Soon").
    if (filters.availability === 'in-stock') {
      // Show only products that have at least one in-warehouse unit available.
      if (!product.hasInStockUnits) return false;
    } else if (filters.availability === 'arriving-soon') {
      // Show only products whose available units are presale-only, plus
      // coming-soon-only models.
      if (product.hasInStockUnits) return false;
      if (!product.hasPresaleUnits && !product.isComingSoonOnly) return false;
    }

    return true;
  })?.sort((a: any, b: any) => {
    // Appliances always appear first, then all other categories; within each group sort by price ascending
    const APPLIANCE_CATS = ['Refrigerators', 'Small Appliances', 'Air Conditioners', 'Dehumidifiers', 'TVs'];
    const aIsAppliance = APPLIANCE_CATS.includes(a.category?.name);
    const bIsAppliance = APPLIANCE_CATS.includes(b.category?.name);
    if (aIsAppliance && !bIsAppliance) return -1;
    if (!aIsAppliance && bIsAppliance) return 1;
    // Coming-soon-only models appear after in-stock models within their category
    if (a.isComingSoonOnly && !b.isComingSoonOnly) return 1;
    if (!a.isComingSoonOnly && b.isComingSoonOnly) return -1;
    return a.startingPrice - b.startingPrice;
  });

  // Extract unique brands (filter by visible categories first)
  const uniqueBrands = allProducts 
    ? Array.from(
        new Set(
          allProducts
            .filter((p: any) => visibleCategories.length === 0 || (p.category?.id && visibleCategories.includes(p.category.id)))
            .map((p: any) => p.brand)
        )
      ).sort()
    : [];

  // Parent category mapping - determines which top-level group each category belongs to
  const parentCategoryMap: Record<string, string> = {
    'Refrigerators': 'Appliances',
    'Small Appliances': 'Appliances',
    'Air Conditioners': 'Appliances',
    'Dehumidifiers': 'Appliances',
    'TVs': 'Appliances',
    'Vacuums': 'Vacuums',
  };
  // Default parent for exercise equipment categories
  const getParentCategory = (categoryName: string) => parentCategoryMap[categoryName] || 'Exercise Equipment';

  // Group brands AND grades by category and parent category for hierarchical display.
  // Each subcategory tracks the brands present in it (constituent brands for
  // pallets) and the cosmetic grades present in its products. The grade row in
  // the sidebar is rendered per-subcategory so e.g. Refrigerators can show
  // A/B+/B- while Air Conditioners (which only ever ships pallet "B") doesn't
  // mix grade scales between unrelated subcategories.
  //
  // Pallets are intentionally excluded from grade-pill aggregation: their
  // single "B" value collides with the unrelated B/B+/B- cosmetic scale used
  // for refrigerator open-box units, and pallets render their own
  // "Factory Refurbished" pill instead. Hiding "B" here also keeps the AC
  // subcategory grade row from appearing at all (it has no other grades),
  // which matches the fix the user asked for.
  const brandsByParentAndCategory = useMemo(() => {
    if (!allProducts) return {};

    const grouped: Record<
      string,
      Record<
        string,
        {
          categoryName: string;
          brands: string[];
          grades: string[];
          brandCounts: Record<string, number>;
          gradeCounts: Record<string, number>;
          totalCount: number;
        }
      >
    > = {};

    allProducts
      .filter((p: any) => visibleCategories.length === 0 || (p.category?.id && visibleCategories.includes(p.category.id)))
      .forEach((p: any) => {
        const catId = p.category?.id || 'uncategorized';
        const catName = p.category?.name || 'Other';
        const parentCat = getParentCategory(catName);

        if (!grouped[parentCat]) {
          grouped[parentCat] = {};
        }

        if (!grouped[parentCat][catId]) {
          grouped[parentCat][catId] = {
            categoryName: catName,
            brands: [],
            grades: [],
            brandCounts: {},
            gradeCounts: {},
            totalCount: 0,
          };
        }
        const block = grouped[parentCat][catId];
        block.totalCount += 1;

        const contributingBrands = getCatalogContributingBrands(p);
        for (const brand of contributingBrands) {
          if (!block.brands.includes(brand)) {
            block.brands.push(brand);
          }
          block.brandCounts[brand] = (block.brandCounts[brand] ?? 0) + 1;
        }

        // Contribute the product's catalog-display grade labels. Factory-
        // refurbished pallets contribute the dedicated PALLET_GRADE_LABEL so
        // the AC subcategory's grade row reads "B — Factory Refurbished"
        // instead of mixing into the unrelated open-box A/B+/B- scale.
        const contributingGrades = getCatalogGradeLabels(p, p.availableGrades);
        for (const g of contributingGrades) {
          if (g && !block.grades.includes(g)) {
            block.grades.push(g);
          }
          if (g) {
            block.gradeCounts[g] = (block.gradeCounts[g] ?? 0) + 1;
          }
        }
      });

    Object.values(grouped).forEach(parent => {
      Object.values(parent).forEach(cat => {
        cat.brands.sort();
        const order = ['A', 'B+', 'B-', PALLET_GRADE_LABEL];
        cat.grades.sort((a, b) => {
          const ai = order.indexOf(a);
          const bi = order.indexOf(b);
          if (ai === -1 && bi === -1) return a.localeCompare(b);
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
      });
    });

    return grouped;
  }, [allProducts, visibleCategories]);

  // Track if we've already applied default filters to avoid re-applying on navigation
  const [hasAppliedDefaults, setHasAppliedDefaults] = useState(false);

  // Set all Appliances + Vacuums subcategories and brands as defaults on first visit
  useEffect(() => {
    // Only run once when inventory loads and we haven't already set defaults
    const hasAppliances = !!brandsByParentAndCategory['Appliances'];
    const hasVacuums = !!brandsByParentAndCategory['Vacuums'];
    if (hasAppliedDefaults || (!hasAppliances && !hasVacuums)) return;
    
    // Check if user has any filters in the URL (not a first visit)
    const searchParams = new URLSearchParams(window.location.search);
    const hasAnyFilter = searchParams.get('brands') || searchParams.get('catBrands') || 
                         searchParams.get('categories') || searchParams.get('grades') ||
                         searchParams.get('warehouses') || searchParams.get('search');
    
    if (hasAnyFilter) {
      setHasAppliedDefaults(true);
      return;
    }
    
    // Build categoryBrands with all Appliances + Vacuums subcategories and their brands
    const defaultCategoryBrands: Record<string, string[]> = {};

    const groupsToInclude = ['Appliances', 'Vacuums'];
    groupsToInclude.forEach(groupName => {
      const groupCategories = brandsByParentAndCategory[groupName];
      if (groupCategories) {
        Object.entries(groupCategories).forEach(([categoryId, data]) => {
          if ((data as any).brands.length > 0) {
            defaultCategoryBrands[categoryId] = [...(data as any).brands];
          }
        });
      }
    });
      
    // Only update if we have brands to set
    if (Object.keys(defaultCategoryBrands).length > 0) {
      updateUrl({ 
        filters: { 
          categoryBrands: defaultCategoryBrands,
          brands: [] // Clear the old default brands since we're using categoryBrands now
        }, 
        resetPage: false 
      });
    }
    
    setHasAppliedDefaults(true);
  }, [brandsByParentAndCategory, hasAppliedDefaults, updateUrl]);

  // Migrate legacy global ?grades= URL (which stored EXCLUDED grades) into the
  // new per-subcategory `categoryGrades` shape (which stores INCLUDED grades).
  // For each visible subcategory, included = its available grades minus the
  // legacy excluded set. We omit subcategories whose included == available so
  // the URL stays clean. Runs once per session.
  const [hasMigratedLegacyGrades, setHasMigratedLegacyGrades] = useState(false);
  useEffect(() => {
    if (hasMigratedLegacyGrades) return;
    if (!brandsByParentAndCategory || Object.keys(brandsByParentAndCategory).length === 0) return;
    const legacyExcluded = filters.grades;
    const perCategory = filters.categoryGrades || {};
    if (!legacyExcluded || legacyExcluded.length === 0) {
      setHasMigratedLegacyGrades(true);
      return;
    }
    if (Object.keys(perCategory).length > 0) {
      // User already has the new per-subcategory shape — drop the stale global
      // grades param to avoid double-application and confusion.
      setHasMigratedLegacyGrades(true);
      updateUrl({ filters: { grades: [] }, resetPage: false });
      return;
    }
    const fanout: Record<string, string[]> = {};
    Object.values(brandsByParentAndCategory).forEach((parent: any) => {
      Object.entries(parent).forEach(([catId, data]: [string, any]) => {
        if (Array.isArray(data?.grades) && data.grades.length > 0) {
          const included = data.grades.filter((g: string) => !legacyExcluded.includes(g));
          // Omit when nothing changed (all still included).
          if (included.length < data.grades.length) {
            fanout[catId] = included;
          }
        }
      });
    });
    setHasMigratedLegacyGrades(true);
    updateUrl({ filters: { categoryGrades: fanout, grades: [] }, resetPage: false });
  }, [hasMigratedLegacyGrades, brandsByParentAndCategory, filters.grades, filters.categoryGrades, updateUrl]);

  // Extract unique categories (filter out null/undefined, dedupe by ID, convert to {id, name})
  // Also filter by visible categories setting
  const uniqueCategories = allProducts 
    ? Array.from(
        new Map(
          allProducts
            .map((p: any) => p.category)
            .filter(Boolean)
            .filter((cat: any) => visibleCategories.length === 0 || visibleCategories.includes(cat.id))
            .map((cat: any) => [cat.id, { id: cat.id, name: cat.name }])
        ).values()
      ).sort((a: any, b: any) => a.name.localeCompare(b.name))
    : [];

  // Pagination calculations
  const totalProducts = filteredProducts?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalProducts / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = filteredProducts?.slice(startIndex, endIndex);

  // Auto-clamp pagination when filtered results shrink
  // Use totalProducts instead of filteredProducts to avoid running on every render
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0 && totalProducts > 0) {
      updateUrl({ pagination: { currentPage: totalPages, itemsPerPage }, resetPage: false });
    }
  }, [totalPages, currentPage, totalProducts, itemsPerPage, updateUrl]);

  // Pagination handlers - update URL directly
  const handlePageChange = (newPage: number) => {
    const clampedPage = Math.max(1, Math.min(newPage, totalPages));
    updateUrl({ pagination: { currentPage: clampedPage, itemsPerPage }, resetPage: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value: string) => {
    const newItemsPerPage = parseInt(value);
    if (VALID_ITEMS_PER_PAGE.includes(newItemsPerPage)) {
      updateUrl({ pagination: { currentPage: 1, itemsPerPage: newItemsPerPage }, resetPage: false });
    }
  };

  // Reset filters - update URL directly.
  //
  // CRITICAL: cancel any pending optimistic-draft flush AND clear the local
  // drafts BEFORE we write the URL. Otherwise a debounced flush queued by a
  // prior brand/grade click can fire ~150 ms after Reset and silently
  // resurrect the just-cleared filters.
  //
  // Memoized so `clearAllFiltersForChips` (and any other callback consumer)
  // can include it in their deps array safely without re-creating on every
  // render.
  const resetFilters = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    setDraftCategoryBrands({});
    setDraftCategoryGrades({});
    updateUrl({
      filters: {
        brands: [],
        categoryBrands: {},
        categories: [],
        grades: [],
        // Per-subcategory grade exclusions must clear too — otherwise toggling
        // off "B — Factory Refurbished" (or any per-cat grade) and then hitting
        // Reset Filters would leave stale catGrades=… in the URL and silently
        // continue to filter the catalog.
        categoryGrades: {},
        warehouseLocations: [],
        sortBy: '',
        priceRange: [catalogPriceBounds.min, catalogPriceBounds.max],
        inStockOnly: false,
        availability: 'all',
        searchTerm: '',
      },
      pagination: { currentPage: 1, itemsPerPage },
      resetPage: false
    });
  }, [updateUrl, catalogPriceBounds.min, catalogPriceBounds.max, itemsPerPage]);

  // ---------------------------------------------------------------------------
  // Optimistic local state for per-cat brands & grades.
  //
  // Why: every URL write triggers `useUrlFilters` to re-parse the query string
  // and `product-gallery` to re-derive its filtered/grouped product memos.
  // With ~100 inventory rows that cascade can be 100–300 ms which is exactly
  // the "click sometimes doesn't register" / "filter is laggy" symptom.
  //
  // Pattern: checkbox handlers update a draft mirror of these two fields
  // synchronously (instant feedback) and a 150 ms debounce flushes the draft
  // to the URL once. Rapid taps collapse to a single URL write and a single
  // re-render of the grid.
  //
  // The drafts re-seed from `filters.*` on URL changes (e.g. browser back),
  // and we cancel any pending debounce before "immediate" writes (Reset, etc.)
  // so we never race a stale draft against a fresh URL state.
  // ---------------------------------------------------------------------------
  const [draftCategoryBrands, setDraftCategoryBrands] = useState<Record<string, string[]>>(filters.categoryBrands);
  const [draftCategoryGrades, setDraftCategoryGrades] = useState<Record<string, string[]>>(filters.categoryGrades);
  const draftCategoryBrandsRef = useRef(draftCategoryBrands);
  const draftCategoryGradesRef = useRef(draftCategoryGrades);
  draftCategoryBrandsRef.current = draftCategoryBrands;
  draftCategoryGradesRef.current = draftCategoryGrades;
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-seed drafts from URL whenever the parsed filter dicts change. The
  // shallow JSON key is stable across our own debounced writes (same content),
  // so the setState is a no-op in that case; only true external URL changes
  // cause a re-seed.
  const filtersBrandsKey = useMemo(
    () => JSON.stringify(filters.categoryBrands),
    [filters.categoryBrands],
  );
  const filtersGradesKey = useMemo(
    () => JSON.stringify(filters.categoryGrades),
    [filters.categoryGrades],
  );
  useEffect(() => {
    setDraftCategoryBrands(filters.categoryBrands);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersBrandsKey]);
  useEffect(() => {
    setDraftCategoryGrades(filters.categoryGrades);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersGradesKey]);

  const cancelPendingDraftFlush = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  const scheduleDraftFlush = useCallback(() => {
    cancelPendingDraftFlush();
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      updateUrl({
        filters: {
          categoryBrands: draftCategoryBrandsRef.current,
          categoryGrades: draftCategoryGradesRef.current,
        },
        resetPage: true,
      });
    }, 150);
  }, [updateUrl, cancelPendingDraftFlush]);

  // On unmount: cancel any pending debounce. We deliberately do NOT flush —
  // if the user is navigating away from the catalog, persisting an in-flight
  // filter click into the next route's URL state isn't useful and risks
  // setState-after-unmount warnings. The 150 ms window is short enough that
  // the worst case is one lost click on rapid navigate-away.
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, []);

  // Toggle a single brand within a subcategory. Optimistic.
  // Empty arrays are preserved (not deleted) so the filtering logic can
  // distinguish "explicitly emptied (hide all)" from "never touched (show all)".
  const toggleCategoryBrand = useCallback((categoryId: string, brand: string) => {
    setDraftCategoryBrands((prev) => {
      const next = { ...prev };
      const cur = next[categoryId] || [];
      next[categoryId] = cur.includes(brand)
        ? cur.filter((b) => b !== brand)
        : [...cur, brand];
      return next;
    });
    scheduleDraftFlush();
  }, [scheduleDraftFlush]);

  // Select all brands in a subcategory.
  const selectAllCategoryBrands = useCallback((categoryId: string, brands: string[]) => {
    setDraftCategoryBrands((prev) => ({ ...prev, [categoryId]: [...brands] }));
    scheduleDraftFlush();
  }, [scheduleDraftFlush]);

  // Clear all brands in a subcategory (explicit empty -> hide all).
  const clearCategoryBrandsInCategory = useCallback((categoryId: string) => {
    setDraftCategoryBrands((prev) => ({ ...prev, [categoryId]: [] }));
    scheduleDraftFlush();
  }, [scheduleDraftFlush]);

  // Toggle a single grade within a subcategory. INCLUDED-list semantics:
  // missing entry = all grades selected by default. First touch seeds the
  // entry with all currently-available grades for this subcat so toggling
  // one off keeps the rest intact. If the result equals all available
  // grades, drop the entry to keep the URL clean.
  const toggleCategoryGrade = useCallback((categoryId: string, grade: string, availableGrades: string[]) => {
    setDraftCategoryGrades((prev) => {
      const next = { ...prev };
      const seeded = categoryId in next ? next[categoryId] : [...availableGrades];
      const updated = seeded.includes(grade)
        ? seeded.filter((g) => g !== grade)
        : [...seeded, grade];
      const isAll = availableGrades.length > 0 && availableGrades.every((g) => updated.includes(g));
      if (isAll) {
        delete next[categoryId];
      } else {
        next[categoryId] = updated;
      }
      return next;
    });
    scheduleDraftFlush();
  }, [scheduleDraftFlush]);

  // Select all grades in a subcategory (drop the entry — "all included" default).
  const selectAllCategoryGrades = useCallback((categoryId: string) => {
    setDraftCategoryGrades((prev) => {
      if (!(categoryId in prev)) return prev;
      const next = { ...prev };
      delete next[categoryId];
      return next;
    });
    scheduleDraftFlush();
  }, [scheduleDraftFlush]);

  // Clear all grades in a subcategory (explicit empty list -> hide all).
  const clearCategoryGradesInCategory = useCallback((categoryId: string) => {
    setDraftCategoryGrades((prev) => ({ ...prev, [categoryId]: [] }));
    scheduleDraftFlush();
  }, [scheduleDraftFlush]);

  // Read helpers — work off the optimistic drafts so checkboxes flip instantly.
  const getCategorySelectedBrands = useCallback((categoryId: string, allBrands: string[]) => {
    if (!(categoryId in draftCategoryBrands)) {
      // Default = nothing selected for this subcat (matches existing default).
      return [] as string[];
    }
    const sel = draftCategoryBrands[categoryId] || [];
    // Filter to only brands that actually exist for this subcat (drops stale).
    return sel.filter((b) => allBrands.includes(b));
  }, [draftCategoryBrands]);

  const getCategorySelectedGrades = useCallback((categoryId: string, allGrades: string[]) => {
    if (!(categoryId in draftCategoryGrades)) {
      // INCLUDED-default: missing entry means "all selected".
      return [...allGrades];
    }
    const sel = draftCategoryGrades[categoryId] || [];
    return sel.filter((g) => allGrades.includes(g));
  }, [draftCategoryGrades]);

  // Clear all category/brand filters at once.
  const clearCategoryBrandFilters = useCallback(() => {
    cancelPendingDraftFlush();
    setDraftCategoryBrands({});
    updateUrl({ filters: { categoryBrands: {} }, resetPage: true });
  }, [cancelPendingDraftFlush, updateUrl]);

  // Clear-all triggered from the active-filter chip bar. resetFilters() now
  // owns its own cancel-pending-flush + clear-drafts logic, so we just
  // delegate. We omit `resetFilters` from deps because it's not memoized
  // (defined fresh each render) and including it would defeat the useCallback;
  // the closure picks up the current ref via lexical scope each render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clearAllFiltersForChips = useCallback(() => {
    resetFilters();
  }, []);

  // Build the chip list for the active-filter bar above the grid. We read
  // from the optimistic drafts so the chips appear/disappear instantly even
  // before the URL flushes. Each chip's onRemove uses the same handlers the
  // sidebar checkboxes use, so removal is consistent across both surfaces.
  const activeFilterChips = useMemo<ActiveChip[]>(() => {
    const chips: ActiveChip[] = [];

    // Per-subcategory brand chips. Skip subcategories where every available
    // brand is selected (this is the "all-selected" default state — showing
    // a chip for every brand would flood the bar with 50+ chips on first
    // visit because of the auto-default-selection effect for Appliances /
    // Vacuums). Only show chips when the user has actively narrowed.
    Object.entries(draftCategoryBrands).forEach(([catId, brands]) => {
      // Find the friendly subcategory name AND its full brand list from our
      // grouped data so we can detect the "all selected" case.
      let subcatName = catId;
      let availableBrands: string[] = [];
      Object.values(brandsByParentAndCategory).some((parent: any) => {
        if (parent[catId]) {
          subcatName = parent[catId].categoryName;
          availableBrands = parent[catId].brands ?? [];
          return true;
        }
        return false;
      });
      // Suppress chips for "all-selected" — same UX intent as the grade
      // chip block below. (If we don't know the universe, fall back to
      // showing every chip rather than silently hiding them.)
      if (availableBrands.length > 0 && brands.length >= availableBrands.length) {
        return;
      }
      brands.forEach((brand) => {
        chips.push({
          key: `brand:${catId}:${brand}`,
          label: `${subcatName} · ${brand}`,
          testId: `chip-brand-${catId}-${brand}`,
          onRemove: () => toggleCategoryBrand(catId, brand),
        });
      });
    });

    // Per-subcategory grade chips — only show when the user narrowed below
    // "all" (i.e. the entry exists AND is shorter than the available grades).
    Object.entries(draftCategoryGrades).forEach(([catId, included]) => {
      let subcatName = catId;
      let availableGrades: string[] = [];
      Object.values(brandsByParentAndCategory).some((parent: any) => {
        if (parent[catId]) {
          subcatName = parent[catId].categoryName;
          availableGrades = parent[catId].grades;
          return true;
        }
        return false;
      });
      if (availableGrades.length === 0) return;
      if (included.length >= availableGrades.length) return; // all selected — no chip
      const label = included.length === 0
        ? `${subcatName} · No conditions`
        : `${subcatName} · ${included.join(', ')}`;
      chips.push({
        key: `grades:${catId}`,
        label,
        testId: `chip-grades-${catId}`,
        onRemove: () => selectAllCategoryGrades(catId),
      });
    });

    if (filters.searchTerm) {
      chips.push({
        key: `search`,
        label: `Search: "${filters.searchTerm}"`,
        testId: `chip-search`,
        onRemove: () => updateUrl({ filters: { searchTerm: '' }, resetPage: true }),
      });
    }
    if (filters.warehouseLocations.length > 0) {
      filters.warehouseLocations.forEach((loc) => {
        chips.push({
          key: `loc:${loc}`,
          label: `Location: ${loc}`,
          testId: `chip-location-${loc}`,
          onRemove: () => updateUrl({
            filters: { warehouseLocations: filters.warehouseLocations.filter((l) => l !== loc) },
            resetPage: true,
          }),
        });
      });
    }
    if (filters.availability && filters.availability !== 'all') {
      chips.push({
        key: `avail`,
        label: `Availability: ${filters.availability}`,
        testId: `chip-availability`,
        onRemove: () => updateUrl({ filters: { availability: 'all' }, resetPage: true }),
      });
    }
    if (filters.inStockOnly) {
      chips.push({
        key: `instock`,
        label: 'In stock only',
        testId: `chip-in-stock`,
        onRemove: () => updateUrl({ filters: { inStockOnly: false }, resetPage: true }),
      });
    }
    const [pmin, pmax] = filters.priceRange;
    if (pmin !== catalogPriceBounds.min || pmax !== catalogPriceBounds.max) {
      chips.push({
        key: `price`,
        label: `Price: $${pmin} – $${pmax}`,
        testId: `chip-price`,
        onRemove: () => updateUrl({
          filters: { priceRange: [catalogPriceBounds.min, catalogPriceBounds.max] },
          resetPage: true,
        }),
      });
    }
    return chips;
  }, [
    draftCategoryBrands,
    draftCategoryGrades,
    brandsByParentAndCategory,
    filters.searchTerm,
    filters.warehouseLocations,
    filters.availability,
    filters.inStockOnly,
    filters.priceRange,
    catalogPriceBounds.min,
    catalogPriceBounds.max,
    toggleCategoryBrand,
    selectAllCategoryGrades,
    updateUrl,
  ]);

  // Toggle filter - update URL directly
  const toggleFilter = (filterKey: string, value: string) => {
    const currentValues = (filters as any)[filterKey] as string[];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    updateUrl({
      filters: { [filterKey]: newValues },
      resetPage: true
    });
  };

  // State for collapsible parent categories (Appliances expanded by default)
  // IMPORTANT: These must be before any conditional returns to avoid React error #300
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    "Appliances": true,
    "Vacuums": true,
  });

  // Per-subcategory open/closed state for the new SubcategoryFilterBlock cards
  // (defaults to open — see `subcatOpen` lookup in render).
  const [subcategoryExpanded, setSubcategoryExpanded] = useState<Record<string, boolean>>({});

  // State for header hover effect (replaces direct DOM manipulation to avoid React conflicts)
  const [hoveredCategory, setHoveredCategory] = useState<{ parentCat: string; categoryName: string } | null>(null);

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Failed to load products. Please try again.</p>
      </div>
    );
  }

  // Count active filters for badge
  const categoryBrandCount = Object.values(filters.categoryBrands || {}).reduce((sum, brands) => sum + brands.length, 0);
  const activeFilterCount = [
    filters.brands.length,
    categoryBrandCount,
    filters.categories.length,
    filters.grades.length,
    filters.warehouseLocations.length,
    filters.inStockOnly ? 1 : 0,
    filters.availability !== 'all' ? 1 : 0,
    (filters.priceRange[0] !== catalogPriceBounds.min || filters.priceRange[1] !== catalogPriceBounds.max) ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const toggleCategoryExpanded = (categoryName: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };
  
  // Update header text and color on hover using React state (not DOM manipulation)
  const handleIconHover = (parentCat: string, categoryName: string) => {
    setHoveredCategory({ parentCat, categoryName });
  };
  
  const handleIconLeave = (_parentCat: string) => {
    setHoveredCategory(null);
  };

  // Filter content component - reused in sidebar and mobile sheet
  const FilterContent = () => (
    <div className="space-y-3">
      {/* Brand Filter - Grouped by Parent Category > Subcategory */}
      <div className="space-y-2">
        {Object.keys(brandsByParentAndCategory).length === 0 ? (
          <p className="text-xs text-muted-foreground">No brands available</p>
        ) : (
          Object.entries(brandsByParentAndCategory)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([parentCat, categories]) => {
              const isExpanded = expandedCategories[parentCat] || false;
              return (
                <div key={parentCat} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => setExpandedCategories(prev => ({
                      ...prev,
                      [parentCat]: !prev[parentCat]
                    }))}
                    className="flex items-center gap-2 w-full py-1 hover:bg-muted/50 rounded transition-colors"
                    data-testid={`toggle-parent-${parentCat.replace(/\s+/g, '-')}`}
                  >
                    <ChevronRight 
                      className={`h-4 w-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                    />
                    <span 
                      className={`font-semibold text-sm md:text-base transition-all duration-150 ${
                        hoveredCategory?.parentCat === parentCat ? 'text-blue-600' : 'text-foreground'
                      }`}
                    >
                      {hoveredCategory?.parentCat === parentCat ? hoveredCategory.categoryName : parentCat}
                    </span>
                  </button>
                  {/* Collapsed view — small icon grid that just expands the
                      parent on click. Clicking does NOT toggle any filters
                      (that fall-through was the source of the "I clicked one
                      icon and a dozen brands lit up" bug). */}
                  {!isExpanded && (
                    <div className="grid grid-cols-4 gap-1 pl-6 mt-1">
                      {Object.entries(categories)
                        .sort(([, a], [, b]) => a.categoryName.localeCompare(b.categoryName))
                        .map(([catId, { categoryName, brands }]) => {
                          const catBrands = draftCategoryBrands[catId] || [];
                          const someBrandsSelected = brands.some((b: string) => catBrands.includes(b));
                          const CategoryIcon = getCategoryIcon(categoryName);
                          const aiIconUrl = categoryIconUrls?.[catId];

                          return (
                            <button
                              key={catId}
                              type="button"
                              onClick={() => setExpandedCategories(prev => ({ ...prev, [parentCat]: true }))}
                              onMouseEnter={() => handleIconHover(parentCat, categoryName)}
                              onMouseLeave={() => handleIconLeave(parentCat)}
                              className={`group flex items-center justify-center aspect-square rounded-lg border-2 overflow-hidden transition-all duration-200 ${
                                someBrandsSelected
                                  ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                                  : 'border-border/40 bg-muted/20 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/30'
                              }`}
                              title={categoryName}
                              data-testid={`collapsed-icon-${catId}`}
                            >
                              {aiIconUrl ? (
                                <img
                                  src={aiIconUrl}
                                  alt={categoryName}
                                  className={`w-full h-full object-cover mix-blend-multiply dark:mix-blend-normal transition-all duration-200 pointer-events-none ${
                                    someBrandsSelected ? 'opacity-100' : 'opacity-60 group-hover:opacity-100'
                                  }`}
                                />
                              ) : (
                                <CategoryIcon className={`h-8 w-8 transition-colors duration-200 pointer-events-none ${
                                  someBrandsSelected ? 'text-green-500 dark:text-green-400' : 'text-muted-foreground group-hover:text-blue-500'
                                }`} />
                              )}
                            </button>
                          );
                        })}
                    </div>
                  )}

                  {/* Expanded view — standard retail-style checkbox panels per
                      subcategory. Each block is internally collapsible, has its
                      own Brands and Condition sub-groups with explicit
                      "Select all · Clear" links, and shows per-option counts.
                      Clicks flow through the optimistic draft state for snappy
                      response, then debounce out to the URL. */}
                  {isExpanded && (
                    <div className="pl-2 space-y-1.5 ml-2">
                      {Object.entries(categories)
                        .sort(([, a], [, b]) => a.categoryName.localeCompare(b.categoryName))
                        .map(([catId, { categoryName, brands, grades, brandCounts, gradeCounts, totalCount }]) => {
                          const CategoryIcon = getCategoryIcon(categoryName);
                          const aiIconUrl = categoryIconUrls?.[catId];
                          const subcatOpen = subcategoryExpanded[catId] ?? true;
                          const selectedBrands = getCategorySelectedBrands(catId, brands);
                          const selectedGrades = getCategorySelectedGrades(catId, grades);
                          return (
                            <SubcategoryFilterBlock
                              key={catId}
                              catId={catId}
                              categoryName={categoryName}
                              brands={brands}
                              grades={grades}
                              selectedBrands={selectedBrands}
                              selectedGrades={selectedGrades}
                              brandCounts={brandCounts}
                              gradeCounts={gradeCounts}
                              totalCount={totalCount}
                              isExpanded={subcatOpen}
                              onToggleExpanded={() => setSubcategoryExpanded(prev => ({ ...prev, [catId]: !(prev[catId] ?? true) }))}
                              onToggleBrand={(brand) => toggleCategoryBrand(catId, brand)}
                              onToggleGrade={(grade) => toggleCategoryGrade(catId, grade, grades)}
                              onSelectAllBrands={() => selectAllCategoryBrands(catId, brands)}
                              onClearBrands={() => clearCategoryBrandsInCategory(catId)}
                              onSelectAllGrades={() => selectAllCategoryGrades(catId)}
                              onClearGrades={() => clearCategoryGradesInCategory(catId)}
                              CategoryIcon={CategoryIcon}
                              aiIconUrl={aiIconUrl}
                            />
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })
          )}
      </div>

      {/* Clear category/brand filters button - only show if filters are active */}
      {categoryBrandCount > 0 && (
        <button 
          onClick={clearCategoryBrandFilters}
          className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
          data-testid="button-clear-category-filters"
        >
          Clear Filters
        </button>
      )}

      <Separator />

      {/* Warehouse Location Filter */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold text-foreground text-sm md:text-base">Location</h3>
        </div>
        <div className="space-y-2">
          {distributionCentersLoading ? (
            <>
              <div className="flex items-center space-x-2">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="flex items-center space-x-2">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-10 w-full" />
              </div>
            </>
          ) : activeWarehouses.length === 0 ? (
            <p className="text-xs text-muted-foreground">No locations available</p>
          ) : (
            activeWarehouses.map((warehouse: any) => (
              <div key={warehouse.id} className="flex items-center gap-2">
                <Checkbox
                  id={`warehouse-${warehouse.warehouseCode}`}
                  checked={filters.warehouseLocations.includes(warehouse.warehouseCode)}
                  onCheckedChange={() => toggleFilter('warehouseLocations', warehouse.warehouseCode)}
                  data-testid={`filter-warehouse-${warehouse.warehouseCode}`}
                />
                <Label 
                  htmlFor={`warehouse-${warehouse.warehouseCode}`} 
                  className={`text-sm cursor-pointer leading-none ${warehouseCodesInCart.includes(warehouse.warehouseCode) ? 'text-green-600 dark:text-green-500' : ''}`}
                >
                  {warehouse.warehouseCode} | {warehouse.city}, {warehouse.region}
                </Label>
              </div>
            ))
          )}
        </div>
      </div>

      <Separator />

      {/* Availability Filter — In Stock Now vs Arriving Soon */}
      <div>
        <h3 className="font-semibold text-foreground mb-3 text-sm md:text-base">Availability</h3>
        <div className="space-y-2" role="radiogroup" aria-label="Availability">
          {([
            { value: 'all', label: 'All' },
            { value: 'in-stock', label: 'In Stock Now' },
            { value: 'arriving-soon', label: 'Arriving Soon' },
          ] as const).map(opt => {
            const isSelected = filters.availability === opt.value;
            return (
              <label
                key={opt.value}
                htmlFor={`availability-${opt.value}`}
                className="flex items-center gap-2 cursor-pointer text-sm leading-none"
              >
                <input
                  type="radio"
                  id={`availability-${opt.value}`}
                  name="availability"
                  value={opt.value}
                  checked={isSelected}
                  onChange={() => updateUrl({ filters: { availability: opt.value }, resetPage: true })}
                  className="h-4 w-4 cursor-pointer accent-primary"
                  data-testid={`radio-availability-${opt.value}`}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      <Separator />

      {/* Price Range Filter */}
      <div>
        <h3 className="font-semibold text-foreground mb-3 text-sm md:text-base">Price Range</h3>
        <Slider
          value={localPriceRange}
          onValueChange={handlePriceRangeChange}
          max={catalogPriceBounds.max}
          min={catalogPriceBounds.min}
          step={50}
          className="mt-2"
          data-testid="filter-price-range"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-2">
          <span>${localPriceRange[0].toFixed(2)}</span>
          <span>${localPriceRange[1].toFixed(2)}</span>
        </div>
      </div>

      <Separator />

      {/* Hide Sold Out */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="inStockOnly"
          checked={filters.inStockOnly}
          onCheckedChange={(checked) => updateUrl({ filters: { inStockOnly: !!checked }, resetPage: true })}
          data-testid="filter-in-stock-only"
        />
        <Label htmlFor="inStockOnly" className="text-sm cursor-pointer leading-none">
          Hide Sold Out
        </Label>
      </div>

      {/* Reset Filters */}
      <Button 
        variant="outline" 
        onClick={resetFilters} 
        className="w-full touch-target"
        data-testid="button-reset-filters"
      >
        Reset All Filters
      </Button>

      <Separator />

      {/* Newsletter Signup - isolated component to prevent scroll issues */}
      <NewsletterSignup />
    </div>
  );

  return (
    <section className="container-responsive py-responsive" style={{ overflowAnchor: 'none' }}>
      {/* Header with Search and View Toggle */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 md:mb-6">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">Open Box Inventory</h2>
        
        {/* Search and View Toggle */}
        <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
          {/* Mobile Filters Button */}
          <Sheet>
            <SheetTrigger asChild>
              <Button 
                variant="outline" 
                className="lg:hidden touch-target relative"
                data-testid="button-mobile-filters"
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-medium">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[350px] overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="mt-4">
                <FilterContent />
              </div>
            </SheetContent>
          </Sheet>

          {/* Search Box */}
          <div className="flex-1 sm:flex-none sm:w-60 md:w-80">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={localSearchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 touch-target"
                data-testid="input-search-products"
              />
            </div>
          </div>

          {/* View Toggle */}
          <ToggleGroup 
            type="single" 
            value={viewMode} 
            onValueChange={(value) => value && setViewMode(value as 'grid' | 'list')} 
            className="hidden sm:flex"
            data-testid="toggle-view-mode"
          >
            <ToggleGroupItem value="grid" aria-label="Grid view" className="touch-target" data-testid="toggle-grid-view">
              <Grid3x3 className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="List view" className="touch-target" data-testid="toggle-list-view">
              <List className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>
      {/* Main Content: Sidebar + Products Grid */}
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        {/* Left Sidebar - Filters (Desktop only) */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <Card>
            <CardContent className="p-4">
              <FilterContent />
            </CardContent>
          </Card>
        </aside>

        {/* Main Content - Products */}
        <div className="flex-1 min-w-0" style={{ overflowAnchor: 'none' }}>
          {/* Hero Carousel - Admin-managed slides OR fallback to Johnson banner (Page 1 only) */}
          {currentPage === 1 && (
            <HeroCarouselWithFallback
              visibleCategoryIds={allCategories?.filter(c => c.visible).map(c => c.id)}
              fallbackImages={productTypeImages?.filter(img => img.imageUrl) || []}
              categoryIconUrls={categoryIconUrls}
              categories={allCategories}
            />
          )}
          
          {/* Active filter chips — shows what's currently filtering the grid
              with one-click removal. Uses the optimistic draft state so the
              chips disappear instantly even before the URL flushes. */}
          <ActiveFilterChips chips={activeFilterChips} onClearAll={clearAllFiltersForChips} />

          {/* Product Grid/List View */}
          {isLoading ? (
            viewMode === 'list' ? (
              <div className="space-y-4">
                {[...Array(8)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        <Skeleton className="w-24 h-24 flex-shrink-0" />
                        <div className="flex-1">
                          <Skeleton className="h-4 w-20 mb-2" />
                          <Skeleton className="h-5 w-48 mb-2" />
                          <Skeleton className="h-4 w-full mb-2" />
                          <Skeleton className="h-4 w-32" />
                        </div>
                        <div className="flex flex-col justify-between items-end">
                          <Skeleton className="h-6 w-24" />
                          <Skeleton className="h-9 w-32" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(8)].map((_, i) => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="w-full h-64" />
                    <CardContent className="p-4">
                      <Skeleton className="h-4 w-16 mb-1" />
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-4 w-full mb-3" />
                      <div className="flex justify-between items-center">
                        <Skeleton className="h-6 w-20" />
                        <Skeleton className="h-9 w-24" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          ) : viewMode === 'list' ? (
            <div className="space-y-4">
              {paginatedProducts?.map((product: any) => {
                // Defensive: Skip products without valid ID
                if (!product?.id) {
                  console.warn('Product missing ID, skipping:', product);
                  return null;
                }
                
                // Build product link with current filter params from filter state
                const filterParams = new URLSearchParams();
                if (filters.warehouseLocations.length > 0) {
                  filterParams.set('warehouses', filters.warehouseLocations.join(','));
                }
                if (filters.grades.length > 0) {
                  filterParams.set('grades', filters.grades.join(','));
                }
                const productHref = filterParams.toString() 
                  ? `/product/${product.id}?${filterParams.toString()}`
                  : `/product/${product.id}`;
                
                // Build specifications list for list view
                const specs: string[] = [];
                
                if (product.dimensionsWidth && product.dimensionsDepth && product.dimensionsHeight) {
                  specs.push(`${product.dimensionsWidth}" W × ${product.dimensionsDepth}" D × ${product.dimensionsHeight}" H`);
                }
                
                if (product.weightLbs) {
                  specs.push(`${parseFloat(product.weightLbs).toFixed(0)} lbs`);
                }
                
                if (product.color) {
                  specs.push(product.color);
                }
                
                // Add key attributes from JSONB — data-driven by category schema (Task #117).
                if (product.attributes) {
                  const attrs = safeParseAttributes(product.attributes);
                  const catRow = product.category
                    ? (allCategories || []).find((c: any) => c?.name === product.category)
                    : undefined;
                  const schema = resolveCategoryAttributeSchema(product.category, catRow?.attributesSchema);
                  for (const field of schema) {
                    const chip = formatAttributeChip(field, attrs[field.key]);
                    if (chip) specs.push(chip);
                  }
                }
                
                const productIndex = paginatedProducts.indexOf(product);
                const handleListItemClick = () => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  trackSelectItem(
                    {
                      id: product.id,
                      modelNumber: product.modelName,
                      brand: product.brand,
                      category: product.category,
                      price: product.startingPrice,
                    },
                    {
                      listId: 'product_gallery',
                      listName: 'Product Gallery',
                      index: productIndex,
                    }
                  );
                };

                return (
                  <Link 
                    key={product.id} 
                    href={productHref}
                    onClick={handleListItemClick}
                  >
                    <Card className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" data-testid={`product-card-${product.id}`}>
                    <CardContent className="p-3 sm:p-4">
                      {/* Mobile: Stack layout, Desktop: Row layout */}
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
                        {/* Image - smaller on mobile */}
                        <div className="w-full sm:w-40 h-40 sm:h-40 flex-shrink-0 bg-muted rounded-md mx-auto sm:mx-0 overflow-hidden" style={{ maxWidth: product.isPallet ? '240px' : '180px' }} data-testid={`product-image-${product.id}`}>
                          {product.isPallet ? (
                            <PalletMiniHero
                              images={product.palletConstituentImages || []}
                              modelCount={product.palletConstituentModelCount}
                              totalUnits={product.palletTotalUnits}
                              badgeSlot={isFactoryRefurbished(product) ? (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-emerald-500 bg-emerald-100 text-emerald-800 shadow-sm"
                                  data-testid={`badge-factory-refurbished-${product.id}`}
                                  title="Factory-refurbished by the manufacturer to like-new spec"
                                >
                                  <Sparkles className="h-2.5 w-2.5" />
                                  {FACTORY_REFURBISHED_LABEL}
                                </span>
                              ) : null}
                            />
                          ) : (
                            <img 
                              src={product.imageUrl || "https://images.unsplash.com/photo-1571175443880-49e1d25b2bc5?w=400&h=400&fit=crop"} 
                              alt={`${product.brand} ${product.modelName}`}
                              className="w-full h-full object-contain rounded-md"
                            />
                          )}
                        </div>
                        
                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <p className="mb-0.5 sm:mb-1 uppercase tracking-wide text-xs sm:text-[14px] text-[#3d4754]" data-testid={`product-brand-${product.id}`}>
                            {getDisplayBrand(product)}
                          </p>
                          <h3 className="font-semibold text-foreground mb-1 sm:mb-2 text-base sm:text-[20px] line-clamp-2 sm:line-clamp-none" data-testid={`product-title-${product.id}`}>
                            {product.modelName}
                          </h3>
                          <p className="text-muted-foreground mb-2 sm:mb-3 line-clamp-2 text-sm sm:text-[16px] hidden sm:block">
                            {product.description}
                          </p>
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {isFactoryRefurbished(product) ? (
                              // For pallets the FR pill is rendered inside
                              // PalletMiniHero next to the "N pcs · M SKUs"
                              // pill, so skip the secondary badge here to
                              // avoid double-rendering.
                              !product.isPallet ? (
                                <span
                                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border-2 border-emerald-500 bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                                  data-testid={`badge-factory-refurbished-info-${product.id}`}
                                  title="Factory-refurbished by the manufacturer to like-new spec"
                                >
                                  <Sparkles className="h-3 w-3" />
                                  {FACTORY_REFURBISHED_LABEL}
                                </span>
                              ) : null
                            ) : (
                              product.availableGrades.map((grade: string) => (
                                <GradeBadge
                                  key={grade}
                                  grade={grade as "A" | "B+" | "B-"}
                                  price={product.gradePrices[grade]}
                                />
                              ))
                            )}
                          </div>
                          {((product.minOrderQty && product.minOrderQty > 1) || (product.orderMultiple && product.orderMultiple > 1)) && (
                            <div className="mt-2 flex items-center" data-testid={`product-ordering-rules-${product.id}`}>
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded px-1.5 py-0.5">
                                {[
                                  product.minOrderQty && product.minOrderQty > 1 ? `Min. ${product.minOrderQty} units` : null,
                                  product.orderMultiple && product.orderMultiple > 1 ? `Order in multiples of ${product.orderMultiple}` : null,
                                ].filter(Boolean).join(' · ')}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Price and CTA */}
                        <div className="flex flex-row sm:flex-col justify-between sm:justify-between items-center sm:items-end mt-2 sm:mt-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-border">
                          <div className="text-left sm:text-right">
                            {product.isComingSoonOnly ? (
                              <>
                                <div className="flex items-center gap-1 mb-1">
                                  <Clock className="w-3.5 h-3.5 text-amber-500" />
                                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">Coming Soon</p>
                                </div>
                                {product.earliestShipDate && (
                                  <p className="text-xs text-muted-foreground" data-testid={`product-price-${product.id}`}>
                                    Ships {format(parseISO(product.earliestShipDate), 'MMM d, yyyy')}
                                  </p>
                                )}
                              </>
                            ) : (
                              <>
                                <p className="text-xs text-muted-foreground">
                                  {product.isPallet ? 'Wholesale' : 'Starting at'}
                                </p>
                                <p className="text-lg sm:text-2xl font-bold text-primary" data-testid={`product-price-${product.id}`}>
                                  {formatMoney(parsePrice(product.startingPrice))}
                                </p>
                                {product.availableQuantity != null && product.availableQuantity > 0 && (
                                  <p
                                    className="text-xs text-muted-foreground"
                                    data-testid={`product-available-quantity-${product.id}`}
                                  >
                                    {product.availableQuantity}{' '}
                                    {product.isPallet
                                      ? product.availableQuantity === 1 ? 'pallet' : 'pallets'
                                      : product.availableQuantity === 1 ? 'unit' : 'units'}{' '}
                                    available
                                  </p>
                                )}
                                {product.isPallet && product.palletPerItemStartingPriceCents != null && product.palletPerItemStartingPriceCents > 0 && (
                                  <p
                                    className="text-xs text-muted-foreground"
                                    data-testid={`product-per-item-price-${product.id}`}
                                  >
                                    from {formatMoney(product.palletPerItemStartingPriceCents / 100)} per item
                                  </p>
                                )}
                                {product.msrp && (
                                  <p className="text-muted-foreground line-through text-sm sm:text-lg font-medium hidden sm:block" data-testid={`product-msrp-${product.id}`}>
                                    MSRP {formatMoney(parsePrice(product.msrp))}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                          <Button 
                            className="bg-primary text-primary-foreground hover:bg-primary/90 touch-target text-sm sm:text-base"
                            data-testid={`button-view-details-${product.id}`}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  </Link>
                );
            })}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
              {paginatedProducts?.map((product: any, index: number) => {
                // Defensive: Skip products without valid ID
                if (!product?.id) {
                  console.warn('Product missing ID, skipping:', product);
                  return null;
                }
                
                return (
                  <ProductCard
                    key={product.id}
                    id={product.id}
                    brand={product.brand}
                    modelName={product.modelName}
                    description={product.description}
                    imageUrl={product.imageUrl}
                    startingPrice={product.startingPrice}
                    availableGrades={product.availableGrades}
                    gradePrices={product.gradePrices}
                    color={product.color}
                    dimensionsWidth={product.dimensionsWidth}
                    dimensionsDepth={product.dimensionsDepth}
                    dimensionsHeight={product.dimensionsHeight}
                    weightLbs={product.weightLbs}
                    msrp={product.msrp}
                    attributes={product.attributes}
                    viewMode="grid"
                    isSoldOut={product.isSoldOut}
                    isComingSoonOnly={product.isComingSoonOnly}
                    earliestShipDate={product.earliestShipDate}
                    category={product.category}
                    minOrderQty={product.minOrderQty}
                    orderMultiple={product.orderMultiple}
                    remoteWarehouseCodes={product.remoteWarehouseCodes}
                    isPallet={product.isPallet}
                    palletConstituentBrands={product.palletConstituentBrands}
                    palletConstituentImages={product.palletConstituentImages}
                    palletConstituentModelCount={product.palletConstituentModelCount}
                    palletTotalUnits={product.palletTotalUnits}
                    palletPerItemStartingPriceCents={product.palletPerItemStartingPriceCents}
                    availableQuantity={product.availableQuantity}
                    productTypeImages={productTypeImages}
                    listContext={{
                      listId: 'product_gallery',
                      listName: 'Product Gallery',
                      index,
                    }}
                  />
                );
            })}
            </div>
          )}

          {/* Pagination Controls */}
          {!isLoading && paginatedProducts && paginatedProducts.length > 0 && (
            <div className="mt-6 md:mt-8 flex flex-col gap-3 sm:gap-4 border-t pt-4 md:pt-6">
              {/* Mobile: Stack vertically, Desktop: Row */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                {/* Items per page selector - hidden on smallest screens */}
                <div className="hidden sm:flex items-center gap-2">
                  <Label className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Per page:</Label>
                  <Select value={String(itemsPerPage)} onValueChange={handleItemsPerPageChange}>
                    <SelectTrigger className="w-16 sm:w-20 touch-target" data-testid="select-items-per-page">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VALID_ITEMS_PER_PAGE.map(value => (
                        <SelectItem key={value} value={String(value)}>{value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Results counter */}
                <div className="text-xs sm:text-sm text-muted-foreground text-center order-first sm:order-none">
                  {startIndex + 1}-{Math.min(endIndex, totalProducts)} of {totalProducts}
                </div>

                {/* Page navigation */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="touch-target px-2 sm:px-3"
                    data-testid="button-prev-page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Prev</span>
                  </Button>

                  <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap px-2">
                    {currentPage} / {totalPages}
                  </span>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="touch-target px-2 sm:px-3"
                    data-testid="button-next-page"
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {filteredProducts?.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No products found matching your filters.</p>
              <Button variant="link" onClick={resetFilters} className="mt-2">
                Reset Filters
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
